import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography } from '@mui/material';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import { TextValidator, Label, Button, ColorPicker, ValidatorForm } from 'components';
import organizationServiceInstance from '../../../../services/OrganizationServices';

const EditOrganizationModal = ({ open, onClose, initialData, onSave }) => {
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationNameSinhala: '',
    organizationNameTamil: '',
    shortName: '',
    color: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
console.log('Initial Data:', initialData);
  useEffect(() => {
    if (open) {
      setFormData({
        organizationName: initialData?.organizationName || initialData?.name || '',
        organizationNameSinhala: initialData?.organizationNameSinhala || initialData?.name_si|| initialData?.sinhala_name || '',
        organizationNameTamil: initialData?.organizationNameTamil || initialData?.name_ta || initialData?.tamil_name ||'',
        shortName: initialData?.shortName || initialData?.short_name || '',
        color: initialData?.color || '',
      });
      setError(null);  
    }
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, color: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!initialData?.id) {
      setError('Organization ID is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateData = {
        name: formData.organizationName,
        name_sinhala: formData.organizationNameSinhala,
        name_tamil: formData.organizationNameTamil,
        short_name: formData.shortName,
        color_code: formData.color,
      };

      // Call the API to update the organization
      const result = await organizationServiceInstance.update(initialData.id, updateData);

      if (result.success) {
        // Call the parent's onSave callback to refresh the data
        onSave(result.data || result);
        onClose();
      } else {
        setError(result?.error || result?.message || 'Failed to update organization');
      }
    } catch (err) {
      console.error('Error updating organization:', err);

      let errorMessage = 'An error occurred while updating the organization';
      if (err.response) {
        if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.statusText) {
          errorMessage = err.response.statusText;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            width: { xs: '100%', md: '700px' },
            maxWidth: { xs: '100%', md: '700px' }
          }
        }}
      >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            border: '2px solid #a83c3c',
            borderRadius: '12%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1
          }}>
            <PriorityHighOutlinedIcon sx={{ color: '#a83c3c', fontSize: 22 }} />
          </Box>
          <Typography variant="h6">Edit the organization</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', border: '1px solid #f44336', borderRadius: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
        <ValidatorForm onSubmit={handleSubmit}>
          <Box sx={{
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: 'unset' },
            gridTemplateColumns: { md: '1fr 1fr' },
            gridTemplateRows: { md: 'auto auto auto' },
            gap: 2,
            mb: 2,
          }}>
            <Box sx={{ gridColumn: { md: '1 / 2' }, gridRow: { md: '1 / 2' } }}>
              <Label htmlFor="organizationName" required>Organization name</Label>
              <TextValidator
                id="organizationName"
                placeholder="Name"
                name="organizationName"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.organizationName}
                onChange={handleChange}
                validators={["required", "maxStringLength:100"]}
                errorMessages={["Organization name is required", "The organization name cannot exceed 100 letters"]}
                disabled={loading}
              />
            </Box>
            <Box sx={{ gridColumn: { md: '2 / 3' }, gridRow: { md: '1 / 2' } }}>
              <Label htmlFor="organizationNameSinhala" required>Organization name (ආයතනයේ නාමය)</Label>
              <TextValidator
                id="organizationNameSinhala"
                placeholder="Name (Sinhala)"
                name="organizationNameSinhala"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.organizationNameSinhala}
                onChange={handleChange}
                validators={["required", "maxStringLength:100"]}
                errorMessages={["Organization name (Sinhala) is required", "The organization name cannot exceed 100 letters"]}
                disabled={loading}
              />
            </Box>
            <Box sx={{ gridColumn: { md: '1 / 3' }, gridRow: { md: '2 / 3' } }}>
              <Label htmlFor="organizationNameTamil" required>Organization name (அமைப்பின் பெயர்)</Label>
              <TextValidator
                id="organizationNameTamil"
                placeholder="Name (Tamil)"
                name="organizationNameTamil"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.organizationNameTamil}
                onChange={handleChange}
                validators={["required", "maxStringLength:100"]}
                errorMessages={["Organization name (Tamil) is required", "The organization name cannot exceed 100 letters"]}
                disabled={loading}
              />
            </Box>
            <Box sx={{ gridColumn: { md: '1 / 2' }, gridRow: { md: '3 / 4' } }}>
              <Label htmlFor="shortName" required>Short name</Label>
              <TextValidator
                id="shortName"
                placeholder="Short name"
                name="shortName"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.shortName}
                onChange={handleChange}
                validators={["required", "maxStringLength:15"]}
                errorMessages={["Organization short name is required", "The short name is too long. Please shorten it"]}
                disabled={loading}
              />
            </Box>
            <Box sx={{ gridColumn: { md: '2 / 3' }, gridRow: { md: '3 / 4' } }}>
              <Label htmlFor="color" required>Color</Label>
              <ColorPicker
                id="color"
                name="color"
                value={formData.color}
                onChange={handleColorChange}
                validators={["required", "matchRegexp:^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"]}
                errorMessages={["Color is required", "Must be valid color code"]}
                disabled={loading}
              />
            </Box>
          </Box>
          <DialogActions sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onClose} variant="outlined" color="error" disabled={loading}>Cancel</Button>
            <Button type="submit" process={loading}>{loading ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </ValidatorForm>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrganizationModal;
